import { z } from 'zod';
import { ROLES, TARGET_METRICS, TARGET_MONTH_PATTERN, TARGET_SCOPES } from '@bulk/types';
import { idSchema, patchOf } from './common';

export const targetMonthSchema = z.string().regex(TARGET_MONTH_PATTERN, 'A month looks like 2026-10');

const targetBaseSchema = z.object({
  month: targetMonthSchema,
  scope: z.enum(TARGET_SCOPES),
  locationId: idSchema.optional().describe('The branch, for a BRANCH target'),
  gymUserId: idSchema.optional().describe('The staff member, for a STAFF target: the id from `GET /targets/sellers`'),
  metric: z.enum(TARGET_METRICS),
  targetValue: z.number().int().positive().max(2_000_000_000).describe('A count, or minor units of money for the revenue metrics'),
});

const namesItsSubject = (input: { scope: (typeof TARGET_SCOPES)[number]; locationId?: string | undefined; gymUserId?: string | undefined }) => {
  if (input.scope === 'GYM') return !input.locationId && !input.gymUserId;
  return input.scope === 'BRANCH' ? Boolean(input.locationId) && !input.gymUserId : Boolean(input.gymUserId) && !input.locationId;
};

export const createTargetSchema = targetBaseSchema.refine(namesItsSubject, {
  message: 'A GYM target names nothing else, a BRANCH target names a locationId and a STAFF target names a gymUserId',
  path: ['scope'],
});
export type CreateTargetInput = z.infer<typeof createTargetSchema>;

export const updateTargetSchema = patchOf(targetBaseSchema.pick({ targetValue: true }));
export type UpdateTargetInput = z.infer<typeof updateTargetSchema>;

export const targetsQuerySchema = z.object({ month: targetMonthSchema.optional().describe("Defaults to the current month on the gym's calendar") });
export type TargetsQuery = z.infer<typeof targetsQuerySchema>;

export const copyTargetsSchema = z
  .object({ fromMonth: targetMonthSchema, toMonth: targetMonthSchema })
  .refine((input) => input.fromMonth !== input.toMonth, { message: 'Copy into a different month', path: ['toMonth'] });
export type CopyTargetsInput = z.infer<typeof copyTargetsSchema>;

export const targetSellerSchema = z.object({
  userId: idSchema,
  gymUserId: idSchema,
  name: z.string(),
  role: z.enum(ROLES),
});
export type TargetSellerDto = z.infer<typeof targetSellerSchema>;

export const TARGET_PACES = ['MET', 'AHEAD', 'BEHIND'] as const;
export type TargetPace = (typeof TARGET_PACES)[number];

export const targetProgressSchema = z.object({
  id: idSchema,
  scope: z.enum(TARGET_SCOPES),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  staff: targetSellerSchema.omit({ role: true }).nullable(),
  metric: z.enum(TARGET_METRICS),
  targetValue: z.number().int(),
  actual: z.number().int().describe('Counted from paid sales dated in the month on the gym calendar, net of refunds'),
  percent: z.number().int().describe('Actual over target, rounded; can pass 100'),
  expected: z.number().int().describe('Target times the days elapsed over the days in the month'),
  pace: z.enum(TARGET_PACES).describe('MET once the target is reached, else AHEAD or BEHIND the expected figure'),
});
export type TargetProgressDto = z.infer<typeof targetProgressSchema>;

export const targetsMonthSchema = z.object({
  month: targetMonthSchema,
  timezone: z.string(),
  currency: z.string(),
  daysElapsed: z.number().int().describe('Days of the month gone, today included; all of them for a past month, none for a future one'),
  daysInMonth: z.number().int(),
  targets: z.array(targetProgressSchema),
});
export type TargetsMonthDto = z.infer<typeof targetsMonthSchema>;

export const copyTargetsResultSchema = z.object({ copied: z.number().int(), skipped: z.number().int() });
export type CopyTargetsResultDto = z.infer<typeof copyTargetsResultSchema>;

export const recreditSaleSchema = z.object({
  soldByUserId: idSchema.nullable().describe('The active staff member of the gym to credit the sale to, from `GET /targets/sellers`; null credits it to nobody'),
});
export type RecreditSaleInput = z.infer<typeof recreditSaleSchema>;

export const saleCreditSchema = z.object({
  orderId: idSchema,
  soldByUserId: idSchema.nullable(),
  soldByName: z.string().nullable(),
});
export type SaleCreditDto = z.infer<typeof saleCreditSchema>;
